import React, { useState, useMemo } from 'react';
import { useAppStore } from '../../store/appStore';
import { cameraApi } from '../../services/api';
import CameraListItem from '../camera/CameraListItem';
import CameraCard from '../camera/CameraCard';
import MapView from './MapView';
import AddCameraModal from '../camera/AddCameraModal';
import AddGroupModal from '../camera/AddGroupModal';

export default function CamerasPanel({ onRefresh }) {
  const { cameras, activeView, searchQuery, filterStatus, filterGroup, selectedCamera, setSelectedCamera, groups, addToast, removeCamera } = useAppStore();
  const [showAdd, setShowAdd] = useState(false);
  const [showAddGroup, setShowAddGroup] = useState(false);

  const filtered = useMemo(() => {
    return cameras.filter(cam => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (!cam.name.toLowerCase().includes(q) && !cam.ipAddress.includes(q) && !cam.location?.toLowerCase().includes(q)) return false;
      }
      if (filterStatus === 'online' && !cam.status?.online) return false;
      if (filterStatus === 'offline' && cam.status?.online) return false;
      if (filterGroup && cam.groupId !== filterGroup) return false;
      return true;
    });
  }, [cameras, searchQuery, filterStatus, filterGroup]);

  const handleDelete = async (cam) => {
    if (!window.confirm(`Delete camera "${cam.name}"?`)) return;
    try {
      await cameraApi.delete(cam.id);
      removeCamera(cam.id);
      addToast({ type: 'success', message: `Camera "${cam.name}" deleted` });
    } catch {
      addToast({ type: 'error', message: 'Failed to delete camera' });
    }
  };

  return (
    <>
      <div style={{
        width: selectedCamera ? '320px' : '100%',
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        borderRight: selectedCamera ? '1px solid var(--border-dim)' : 'none',
        overflow: 'hidden',
        transition: 'width 0.2s ease',
      }}>
        {/* Header */}
        <div style={{
          padding: '12px 16px',
          borderBottom: '1px solid var(--border-dim)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: '11px', fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>
            {filtered.length} of {cameras.length} Cameras
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setShowAddGroup(true)}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16"/></svg>
              Group
            </button>
            <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(true)}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Add Camera
            </button>
          </div>
        </div>

        {/* Camera list/grid/map */}
        <div style={{ flex: 1, overflow: 'hidden' }}>
          {activeView === 'list' && (
            <div className="scrollable" style={{ height: '100%' }}>
              {filtered.length === 0 ? (
                <EmptyState onAdd={() => setShowAdd(true)} />
              ) : (
                filtered.map(cam => (
                  <CameraListItem
                    key={cam.id}
                    camera={cam}
                    selected={selectedCamera?.id === cam.id}
                    onClick={() => setSelectedCamera(selectedCamera?.id === cam.id ? null : cam)}
                    onDelete={() => handleDelete(cam)}
                    groups={groups}
                  />
                ))
              )}
            </div>
          )}

          {activeView === 'grid' && (
            <div className="scrollable" style={{ height: '100%', padding: '12px' }}>
              {filtered.length === 0 ? (
                <EmptyState onAdd={() => setShowAdd(true)} />
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '12px' }}>
                  {filtered.map(cam => (
                    <CameraCard
                      key={cam.id}
                      camera={cam}
                      selected={selectedCamera?.id === cam.id}
                      onClick={() => setSelectedCamera(selectedCamera?.id === cam.id ? null : cam)}
                      groups={groups}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {activeView === 'map' && (
            <MapView cameras={filtered} selectedCamera={selectedCamera} onSelectCamera={setSelectedCamera} />
          )}
        </div>
      </div>

      {showAdd && (
        <AddCameraModal
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); onRefresh(); }}
        />
      )}

      {showAddGroup && (
        <AddGroupModal
          onClose={() => setShowAddGroup(false)}
          onSaved={() => { setShowAddGroup(false); onRefresh(); }}
        />
      )}
    </>
  );
}

function EmptyState({ onAdd }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '16px', padding: '40px', color: 'var(--text-muted)' }}>
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.4">
        <circle cx="12" cy="8" r="5"/><circle cx="12" cy="12" r="11" stroke="currentColor" strokeWidth="1" opacity="0.4"/>
      </svg>
      <div style={{ fontFamily: 'var(--font-display)', fontSize: '16px', letterSpacing: '0.1em', textAlign: 'center', textTransform: 'uppercase' }}>
        No cameras found
      </div>
      <button className="btn btn-primary btn-sm" onClick={onAdd}>Add Your First Camera</button>
    </div>
  );
}
